


























































































































































import { PropType } from 'vue';
import { union, orderBy, get, sumBy, filter, isEmpty, pick, forEach, map, set } from 'lodash';

import CoHeadline from '../../Atoms/co-headline/CoHeadline.vue';
import CoIcon from '../../Atoms/co-icon/CoIcon.vue';
import CoButton from '../../Atoms/co-button/CoButton.vue';
import CoDownloadAsCsv from '../../Organisms/co-download-as-csv/CoDownloadAsCsv.vue';
import CoAlert from '../../Molecules/co-alert/CoAlert.vue';
import CoSkeleton from '../../Atoms/co-skeleton/CoSkeleton.vue';
import CoRoundButton from '../../Atoms/co-round-button/CoRoundButton.vue';

interface Column {
    title: string;
    key: string;
    notSortable?: boolean;
    hidden?: boolean;
}

interface OrderCall {
    key: string;
    asc: boolean;
}

export default {
    components: {
        CoHeadline,
        CoIcon,
        CoButton,
        CoDownloadAsCsv,
        CoAlert,
        CoSkeleton,
        CoRoundButton,
    },
    name: 'CoTable',
    props: {
        items: {
            type: Array as PropType<Object[]>,
            default() {
                return [];
            },
        },
        columns: {
            type: Array as PropType<Column[]>,
            default() {
                return [];
            },
        },
        striped: {
            type: Boolean,
            default: false,
        },
        itemsPerPage: {
            type: Number,
            default: 10,
        },
        autoItemsPerPage: {
            type: Boolean,
            default: false,
        },
        loading: {
            type: Boolean,
            default: false,
        },
        sortable: {
            type: Boolean,
            default: true,
        },
        showItemsCount: {
            type: Boolean,
            default: true,
        },
        preventHorizontalScroll: {
            type: Boolean,
            default: false,
        },
        fixedFirstColumn: {
            type: Boolean,
            default: false,
        },
        fixedFirstRow: {
            type: Boolean,
            default: false,
        },
        resizableColumns: {
            type: Boolean,
            default: false,
        },
        csvDownload: {
            type: Boolean,
            default: false,
        },
        csvDownloadOnlyDisplayedItems: {
            type: Boolean,
            default: true,
        },
        title: {
            type: String,
            default: '',
        },
        info: {
            type: String,
            default: '',
        },
        serverHasMoreItems: {
            type: Boolean,
            default: false,
        },
        clickableRows: {
            type: Boolean,
            default: false,
            required: false,
        },
    },
    data() {
        return {
            orderedByColumn: null,
            order: 'asc',
            orderedItems: this.items,
            currentPage: 1,
            dragStartX: null,
            dragStartColWidth: null,
            draggedTh: null,
            dragger: null,
            draggerStartX: null,
            lastColumnLayoutSignature: '',

            localItemsPerPage: this.itemsPerPage,
        };
    },
    computed: {
        locCols() {
            if (this.columns && this.columns.length > 0) {
                return this.columns;
            }
            // create an inventory of all unique keys to build columns accordingly
            let keys = [];
            this.items.forEach((element) => {
                keys = union(keys, Object.keys(element));
            });
            // return title,key objects
            return keys.map((i) => ({ title: i, key: i }));
        },
        visibleCols() {
            // Keep rendered headers, cells, and resize draggers aligned when some columns are hidden.
            return this.locCols.filter((column) => !column.hidden);
        },
        itemsCount() {
            return this.items.length;
        },
        pagesCount() {
            if (!this.itemsCount || !this.localItemsPerPage) return null;
            return Math.ceil(this.itemsCount / this.localItemsPerPage);
        },
        displayedItems() {
            if (!this.pagesCount || this.pagesCount < 2) return this.orderedItems;
            return this.orderedItems.slice(
                this.currentPage * this.localItemsPerPage - this.localItemsPerPage,
                this.currentPage * this.localItemsPerPage
            );
        },
        sums() {
            let sumObj = {};
            filter(this.locCols, ['sum', true]).forEach((i) => {
                sumObj[i.key] = sumBy(this.items, (o) => {
                    if (o[i.key] && parseFloat(o[i.key])) {
                        return parseFloat(o[i.key]);
                    }
                });
            });
            return isEmpty(sumObj) ? null : sumObj;
        },
        colClasses() {
            // create an inventory of all unique keys to build columns accordingly
            var classesObj = {};
            this.locCols.map((element) => {
                let classes = element.class ? element.class.split(' ') : [];
                element.important ? classes.push('important') : null;
                element.notSortable ? classes.push('not-sortable') : null;
                classes.forEach((i) => {
                    set(classesObj, `[${element.key}][${i}]`, true);
                });
            });
            return classesObj;
        },
        colStyles() {
            // create an inventory of all unique keys to build columns accordingly
            var stylesObj = {};
            this.locCols.map((element) => {
                let styles = element.style ? element.style : '';
                stylesObj[element.key] = styles;
            });
            return stylesObj;
        },
    },
    watch: {
        itemsPerPage(newVal) {
            this.localItemsPerPage = newVal;
            this.currentPage = 1; // reset to page 1 when itemsPerPage changes
        },
        items(newVal, oldVal) {
            this.orderedItems = newVal;
            if (newVal.length < oldVal.length) {
                this.currentPage = 1;
            }
        },
    },
    emits: ['order'],
    mounted() {
        this.localItemsPerPage = this.itemsPerPage;
        this.$nextTick(function () {
            // Code that will run only after the
            // entire view has been rendered
            this.updateColumnPosition();
            window.addEventListener('resize', this.updateColumnPosition);
            if (this.autoItemsPerPage) {
                this.calNumberOfItemsPerPage();
                window.addEventListener('resize', this.calNumberOfItemsPerPage);
            }
        });
    },
    updated() {
        this.$nextTick(() => {
            // Code that will run only after the
            // entire view has been rendered
            this.updateColumnPosition();
            if (this.autoItemsPerPage) {
                this.calNumberOfItemsPerPage();
            }
        });
    },
    beforeDestroy() {
        window.removeEventListener('resize', this.updateColumnPosition);
        window.removeEventListener('resize', this.calNumberOfItemsPerPage);
    },
    methods: {
        get, // lodash get
        getConfiguredColumnWidth(column, fallbackWidth) {
            const widthMatch = column?.style?.match(/width:\s*([^;]+)/i);
            if (!widthMatch || widthMatch.length < 2) {
                return fallbackWidth;
            }

            // Reuse the declarative column width when columns are toggled so temporary stretched widths do not persist.
            return widthMatch[1].replace(/\s*!important/i, '').trim();
        },
        getColumnLayoutSignature() {
            return this.locCols.map((column) => `${column.key}:${column.hidden ? 'hidden' : 'visible'}`).join('|');
        },
        clickRow(index, item) {
            if (!this.clickableRows) return;

            this.$emit('clickedRow', index, item);
        },
        orderItems(orderkey) {
            if (!orderkey || typeof orderkey !== 'string') return false;
            // jump to page 1 when ordering changes
            this.currentPage = 1;

            if (this.orderedByColumn === orderkey) {
                this.order = this.order === 'asc' ? 'desc' : 'asc';
            } else {
                this.orderedByColumn = orderkey;
                this.order = 'asc';
            }

            let that = this;
            this.orderedItems = orderBy(
                this.orderedItems,
                (o) => {
                    let orderType = typeof get(o, orderkey, '');
                    switch (orderType) {
                        case 'number':
                            return get(o, orderkey, '');
                        case 'string':
                        case 'boolean':
                            return get(o, orderkey, '').toString().toLowerCase();
                        default:
                            return false;
                    }
                },
                [that.order]
            );

            this.$emit('order', {
                key: orderkey,
                asc: this.order === 'asc',
            } as OrderCall);

            return true;
        },

        nextPage() {
            // currentPage < pagesCount ? currentPage++ : null
            if (this.currentPage < this.pagesCount) {
                this.currentPage += 1;
            }

            this.$emit('nextPage');
            return true;
        },

        getValue(item, key) {
            if (!item || !key || typeof key !== 'string') return null;
            // if key is a nested object, return the value using lodash get
            const value = get(item, key, '');
            // if value is an object, return string with key value pairs
            if (value && typeof value === 'object') {
                if (Array.isArray(value)) {
                    return value.join(', ');
                }
                return Object.entries(value)
                    .map(([k, v]) => `${k}: ${v}`)
                    .join(', ');
            }
            return value;
        },

        getIcon(columnKey) {
            if (this.orderedByColumn === columnKey && this.order === 'asc') return 'arrow-up-short';
            if (this.orderedByColumn === columnKey && this.order === 'desc') return 'arrow-down-short';
            return 'chevron-expand';
        },
        columnDragStart(event, index) {
            const resizeContainer = this.$refs.resizeContainer;
            const table = this.$refs.table;
            const th = this.$el.querySelector(`th:nth-of-type(${index + 1})`);

            this.dragger = event.target;
            this.draggerStartX = event.target.offsetLeft;
            this.dragStartColWidth = th.offsetWidth;
            this.dragStartX = event.pageX;
            this.draggedTh = th;

            this.dragger.classList.add('active');

            document.body.style.cursor = 'col-resize';
            document.body.style.userSelect = 'none';

            resizeContainer.addEventListener('mousemove', this.columnDrag);
            table.addEventListener('mousemove', this.columnDrag);
            document.addEventListener('mouseup', this.columnDragEnd);
        },
        columnDrag(event) {
            const distanceX = event.pageX - this.dragStartX;
            const th = this.draggedTh;
            if (th && th.style && this.dragStartColWidth - 50 > -distanceX) {
                this.dragger.style.left = this.draggerStartX + distanceX + 'px';
                th.style.width = this.dragStartColWidth + distanceX + 'px';
            }
        },
        columnDragEnd(event) {
            const resizeContainer = this.$refs.resizeContainer;
            const table = this.$refs.table;

            this.dragger.classList.remove('active');

            document.body.style.cursor = '';
            document.body.style.userSelect = '';

            document.removeEventListener('mouseup', this.columnDragEnd);
            resizeContainer.removeEventListener('mousemove', this.columnDrag);
            table.removeEventListener('mousemove', this.columnDrag);
            this.updateColumnPosition();
        },
        updateColumnPosition() {
            if (!this.resizableColumns) return;
            if (!this.$refs.table) return;
            const tableDimensions = this.$refs.table.getBoundingClientRect();
            const ths = this.$el.querySelectorAll('th');
            const draggers = this.$el.querySelectorAll('.columns-dragger');
            const columnLayoutSignature = this.getColumnLayoutSignature();
            const hasColumnLayoutChanged = this.lastColumnLayoutSignature !== columnLayoutSignature;
            var columWidth = 0;
            forEach(ths, (th, index) => {
                const dragger = draggers[index];
                const column = this.visibleCols[index];
                if (!dragger || !column) {
                    return;
                }
                // set min width for columns
                const offsetWidth = th.offsetWidth > 100 ? th.offsetWidth : 100;
                const configuredWidth = this.getConfiguredColumnWidth(column, offsetWidth + 'px');
                const nextWidth = hasColumnLayoutChanged ? configuredWidth : offsetWidth + 'px';
                const normalizedWidth = parseFloat(nextWidth) > 100 ? nextWidth : '100px';
                columWidth += parseFloat(normalizedWidth);
                dragger.style.top = '0';
                dragger.style.height = tableDimensions.height + 'px';
                dragger.style.left = columWidth - 5 + 'px';
                th.style.width = normalizedWidth;
            });
            this.lastColumnLayoutSignature = columnLayoutSignature;
        },
        getTableData() {
            const columns = map(this.locCols, 'key');
            const data = [];
            if (this.csvDownloadOnlyDisplayedItems) {
                forEach(this.orderedItems, (value, key) => {
                    data.push(pick(value, columns));
                });
            } else {
                forEach(this.items, (value, key) => {
                    data.push(value);
                });
            }
            return data;
        },
        downloadCsv() {
            const csvData = this.getTableData();
            this.$refs.csvDownloader.exportAsCSV(csvData);
        },

        calNumberOfItemsPerPage() {
            if (this.autoItemsPerPage) {
                const { wrapper } = this.$refs;
                if (wrapper) {
                    const wrapperTop = wrapper.getBoundingClientRect().top;
                    // Use the remaining viewport below the table wrapper so dynamic content above
                    // the table does not artificially reduce the calculated row count.
                    const wrapperHeight = window.innerHeight - wrapperTop;
                    // const heightOfFooter = 52; // Approximate height of the footer
                    const scrollBarHeight = 17; // Approximate height of the scrollbar

                    const usableHeight = wrapperHeight - scrollBarHeight;
                    const rowHeight = 49; // Approximate height of a table row
                    const nextItemsPerPage = Math.max(1, Math.floor(usableHeight / rowHeight));

                    if (this.localItemsPerPage !== nextItemsPerPage) {
                        this.localItemsPerPage = nextItemsPerPage;
                    }
                }
            }
        },
    },
};
